import {
  AssessmentChartCardProps,
  AssessmentChartLabelToValueMap,
  AssessmentDisplayStatus,
  AssessmentListCardItem,
} from '@equip.health/ui';
import { AvatarUserType } from '@equip.health/ui/lib/types/src/components/Avatar';
import { DateTime } from 'luxon';

import { AssessmentChartDataScoreStrategyMap } from '~/lib/constants/survey/AssessmentChartDataScoreStrategyMap';
import { SurveyResponseStatusToDisplayStatusMap } from '~/lib/constants/survey/SurveyResponseStatusToDisplayStatusMap';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';

import { getSurveyCompletionPercentage } from './survey.util';
import { AssessmentListDataScoreStrategyMap } from '../constants/survey/AssessmentListDataScoreStrategyMap';
import { SurveyNameToListCardTitlesMap } from '../constants/survey/SurveyNameToListCardTitlesMap';
const getUpdatedByUserName = (assessment: SurveyResponse): string => {
  const isSubmittedUserDisplayNameAvailable =
    assessment.submittedUserDisplayFirstName &&
    assessment.submittedUserDisplayLastName;

  return isSubmittedUserDisplayNameAvailable
    ? `${assessment.submittedUserDisplayFirstName} ${assessment.submittedUserDisplayLastName}`
    : assessment.submittedUserName || '';
};

export const adaptNonScoreAssessmentData = (
  assessment: SurveyResponse,
): Pick<
  AssessmentChartLabelToValueMap,
  | 'assessmentResponseId'
  | 'assignee'
  | 'epochCreatedDate'
  | 'epochCompletedOrUpdatedDate'
  | 'status'
  | 'completedOrUpdatedBy'
  | 'completedOrUpdatedByUserAvatarSrc'
  | 'completedOrUpdatedByUserType'
  | 'completedOrUpdatedByUserId'
> => {
  const assignee =
    assessment.toUserFirstName && assessment.toUserLastName
      ? `${assessment.toUserFirstName} ${assessment.toUserLastName}`
      : undefined;

  return {
    assessmentResponseId: assessment.responseExternalId,
    assignee,
    completedOrUpdatedBy: getUpdatedByUserName(assessment),
    completedOrUpdatedByUserAvatarSrc:
      assessment.submittedUserAvatarDetails?.thumbnailUrl ||
      assessment.submittedUserAvatarDetails?.url,
    completedOrUpdatedByUserId: assessment.submittedUserId,
    completedOrUpdatedByUserType:
      assessment.submittedUserType as AvatarUserType,
    epochCompletedOrUpdatedDate: DateTime.fromISO(
      assessment.updatedDate, // technically nullable, but 0 records in prod with null. not handling the error case atm
    ).valueOf(),
    epochCreatedDate: DateTime.fromISO(assessment.createdDate).valueOf(),
    status: SurveyResponseStatusToDisplayStatusMap[assessment.status],
  };
};

export const deriveChartData = (
  groupedAssessment: GroupedAssessment,
  patientGender: string, // needed for calculating DUDIT
): AssessmentChartCardProps['chartData'] => {
  return groupedAssessment.assessments
    .map((assessment) => {
      const displayStatus =
        SurveyResponseStatusToDisplayStatusMap[assessment.status];

      return {
        ...adaptNonScoreAssessmentData(assessment),
        ...(displayStatus === AssessmentDisplayStatus.COMPLETE &&
          AssessmentChartDataScoreStrategyMap[
            groupedAssessment.name
          ].deriveScores(assessment, patientGender)),
      };
    })
    .sort(
      (a, b) => a.epochCompletedOrUpdatedDate - b.epochCompletedOrUpdatedDate,
    );
};

export const getAssessmentListCardItems = (
  groupedAssessment: GroupedAssessment,
): AssessmentListCardItem[] => {
  return groupedAssessment.assessments
    .map((assessment) => {
      let scoresAndRisks = {};
      if (AssessmentListDataScoreStrategyMap[groupedAssessment.name]) {
        scoresAndRisks =
          AssessmentListDataScoreStrategyMap[
            groupedAssessment.name
          ].deriveScores(assessment);
      }
      return {
        ...scoresAndRisks,
        ...(assessment.toUserFirstName &&
          assessment.toUserLastName && {
            assignedTo: `${assessment.toUserFirstName} ${assessment.toUserLastName}`,
          }),
        completedBy: assessment.submittedUserName, // todo is there an issue if it's not completed?
        completionPercentage: getSurveyCompletionPercentage(
          assessment.surveyDefinition,
          assessment.jsonResults,
        ),
        createdDate: DateTime.fromISO(assessment.createdDate).toFormat(
          "LL/dd/yyyy 'at' hh:mma",
        ), // TODO this is a legacy format, pass it in a simpler format
        endDate: DateTime.fromISO(assessment.endDate).toFormat(
          "LL/dd/yyyy 'at' hh:mma",
        ),
        externalId: assessment.responseExternalId,
        name: groupedAssessment.name,
        responseUpdatedBy: getUpdatedByUserName(assessment),
        responseUpdatedByAvatarSrc:
          assessment.submittedUserAvatarDetails?.thumbnailUrl ||
          assessment.submittedUserAvatarDetails?.url,
        responseUpdatedByUserId: assessment.submittedUserId,
        responseUpdatedByUserType:
          assessment.submittedUserType as AvatarUserType,
        rows: SurveyNameToListCardTitlesMap[groupedAssessment.name],
        status: SurveyResponseStatusToDisplayStatusMap[assessment.status],
        submittedDate: DateTime.fromISO(assessment.updatedDate).toFormat(
          "LL/dd/yyyy 'at' hh:mma",
        ),
      };
    })
    .slice(0, 4);
};
